import { EventObject } from '@xstate/store-react';
import { StepKey } from '../../enums/stepKey';
import { FormKey } from '../../enums/formKey';
import { StoreContext } from '../../models/store/context';
import { getPreviousStepPath } from '../../routes/routes';
import { log } from '@domgen/shared/utils/log';
import { AppRoute } from '../../enums/appRoutes';
import { StoreEvent } from '../../enums/storeEvent';
import { getCategoryFromApplianceCodeInCatalogue } from '../../utils/store/getCategoryFromApplianceCodeInCatalogue';
import { findApplianceFromResults } from '@domgen/shared/utils/findApplianceFromResults';

/**
 * @description Back Event Handler
 * This event handler is responsible for handling the "back" event in the application. When this event is triggered, it takes the current step key from the event data, determines the previous step's path using the getPreviousStepPath function, and then navigates the user to that previous step. If a previous step path is found, it uses the enqueue.emit.navigate function to change the route to the previous step. Finally, it returns the unchanged context since this event does not modify any state in the context.
 * @param {StoreContext} context - The current store context
 * @param {EventObject & { data: { stepKey: StepKey } }} event - The event object containing the current step key in event.data
 * @param {{ effect: (effect: () => Promise<void>) => void; emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void } }} enqueue - An object with an effect function to enqueue side effects and an emit function to navigate to a different route
 * @returns {StoreContext} The unchanged context after handling the back event
 */
export const back = <T extends EventObject = EventObject>(
  context: StoreContext,
  event: T & { data: { stepKey: StepKey } },
  enqueue: {
    effect: (effect: () => Promise<void>) => void;
    emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void };
  },
): StoreContext => {
  log.debug({ message: StoreEvent.BACK, data: event.data });

  // A recognised model auto-fills appliance details and skips that page (landing -> price).
  // So going back from the price page must return to the landing page, not to the appliance
  // details page the user never saw. Mirrors the Whirlpool back flow.
  const modelNumber = context.form?.[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER];
  const matchedAppliance = findApplianceFromResults(
    modelNumber as string,
    context.applianceLookupResults ?? [],
  );
  const applianceMatchedInLookup = Boolean(
    matchedAppliance &&
    getCategoryFromApplianceCodeInCatalogue(
      matchedAppliance.itemTypeCode,
      context.app?.catalogue ?? undefined,
    ),
  );

  if (event.data.stepKey === StepKey.PURCHASE_PRICE && applianceMatchedInLookup) {
    enqueue.emit.navigate({ path: AppRoute.LANDING, replace: true });
    return context;
  }

  const previousStep = getPreviousStepPath(event.data.stepKey);
  if (previousStep) {
    enqueue.emit.navigate({ path: previousStep });
  }
  return context;
};
